/** One field block of the booking batch-edit dialog: the field selector first
 *  (conditional-fields rule), then a value editor chosen by the catalog widget,
 *  the "next sync" badge, and the field's warnings — tenant scope, bespoke tour,
 *  cancellation mode, old-tour tags, order link.
 */
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker, DateTimePicker, TimePicker } from '@mui/x-date-pickers-pro';
import { useCallback } from 'react';

import type {
  BookingEditCatalog,
  BookingEditCatalogField,
  BookingEditRoleOption,
  BookingEditTourOption,
  CancelMode,
} from '@/api/bookingEdit';
import { rolePickerOptions, tourPickerOptions } from '@/api/bookingEdit';
import type { BatchActionContext } from '@/components/DataGrid/batchTypes';
import { detectCrossTenant } from '@/components/DataGrid/detectCrossTenant';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';

import {
  BESPOKE_BLOCKED_FIELDS,
  STATE_CANCELLED,
  bespokeTarget,
  disabledReason,
  fieldOf,
  type BlockDraft,
  type RunOptions,
} from './model';

const SYNC_BADGE: Record<BookingEditCatalogField['sync'], { label: string; color: 'warning' | 'default' | 'success' }> = {
  reverts: { label: 'Reverted by next sync', color: 'warning' },
  partial: { label: 'May be reverted by sync', color: 'default' },
  keeps: { label: 'Kept on sync', color: 'success' },
};

const CANCEL_MODE_WARNING: Record<CancelMode, string> = {
  cancel_with_event:
    'Runs the cancellation procedure (removes the guest from the tour chat, cascades to sub-bookings) and sends the cancellation event — cancellation notifications may go out.',
  cancel_without_event:
    'Runs the cancellation procedure without the cancellation event — no cancellation notifications.',
  silent_state:
    'Only sets the state: no chat removal, no sub-booking cascade, no cancellation event, and no update event for these bookings.',
};

export interface FieldBlockProps {
  index: number;
  block: BlockDraft;
  blocks: BlockDraft[];
  catalog: BookingEditCatalog;
  ctx: BatchActionContext;
  opts: RunOptions;
  onOptsChange: (patch: Partial<RunOptions>) => void;
  /** Tenant to scope the tour / role pickers to (grid tenant, else the other scoped value's). */
  pickerTenantId: number | null;
  canRemove: boolean;
  onFieldChange: (field: string) => void;
  onChange: (patch: Partial<BlockDraft>) => void;
  onRemove: () => void;
}

export function FieldBlock({
  index,
  block,
  blocks,
  catalog,
  ctx,
  opts,
  onOptsChange,
  pickerTenantId,
  canRemove,
  onFieldChange,
  onChange,
  onRemove,
}: FieldBlockProps) {
  const spec = fieldOf(catalog, block.field);
  const disabled = disabledReason(block, blocks);
  const bespoke = bespokeTarget(blocks);
  const usedElsewhere = new Set(blocks.filter((b) => b.id !== block.id && b.field).map((b) => b.field as string));

  const tourQuery = useCallback(
    (q: string, signal: AbortSignal) => tourPickerOptions(q, pickerTenantId, signal),
    [pickerTenantId],
  );
  const roleQuery = useCallback(
    (q: string, signal: AbortSignal) => rolePickerOptions(q, pickerTenantId, signal),
    [pickerTenantId],
  );

  const valueTenantId =
    block.field === 'tour'
      ? (block.tour?.tenant?.id ?? null)
      : block.field === 'role' && !block.roleClear
        ? (block.role?.tenant?.id ?? null)
        : null;
  const crossTenant =
    spec?.tenant_scoped && valueTenantId != null
      ? detectCrossTenant(valueTenantId, ctx.selection, {
          rows: ctx.rowTenantIds.map((tenantId) => ({ tenantId })),
          gridTenantFilter: ctx.tenantFilterId,
        })
      : false;
  const valueTenantCode = block.field === 'tour' ? block.tour?.tenant?.code : block.role?.tenant?.code;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, opacity: disabled ? 0.65 : 1 }}>
      <Stack spacing={1.25}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ flexGrow: 1 }}>
            Field {index + 1}
          </Typography>
          {spec && (
            <Tooltip title={spec.sync_note || ''}>
              <Chip size="small" variant="outlined" color={SYNC_BADGE[spec.sync].color} label={SYNC_BADGE[spec.sync].label} />
            </Tooltip>
          )}
          {canRemove && (
            <IconButton size="small" aria-label={`Remove field ${index + 1}`} onClick={onRemove}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        <TextField
          select
          size="small"
          label="Field"
          value={block.field ?? ''}
          onChange={(e) => onFieldChange(e.target.value)}
          fullWidth
        >
          {catalog.fields.map((f) => {
            const blockedByBespoke = bespoke != null && (BESPOKE_BLOCKED_FIELDS as readonly string[]).includes(f.key);
            const used = usedElsewhere.has(f.key);
            return (
              <MenuItem key={f.key} value={f.key} disabled={used || blockedByBespoke}>
                <ListItemText
                  primary={f.label}
                  secondary={used ? 'already in this run' : blockedByBespoke ? 'disabled — the target tour is bespoke' : undefined}
                />
              </MenuItem>
            );
          })}
        </TextField>

        {disabled && <Alert severity="warning">{disabled}</Alert>}

        {spec && !disabled && (
          <>
            {spec.ops.includes('shift') && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={block.op}
                onChange={(_, v) => v && onChange({ op: v, shift: '', when: null })}
                aria-label="Operation"
              >
                <ToggleButton value="set">Set to</ToggleButton>
                <ToggleButton value="shift">Shift by</ToggleButton>
              </ToggleButtonGroup>
            )}

            <ValueEditor
              spec={spec}
              block={block}
              onChange={onChange}
              tourQuery={tourQuery}
              roleQuery={roleQuery}
            />

            {spec.help && (
              <Typography variant="caption" color="text.secondary">
                {spec.help}
              </Typography>
            )}
            {spec.warning && block.field !== 'tour' && (
              <Typography variant="caption" color="warning.main">
                {spec.warning}
              </Typography>
            )}

            {crossTenant && (
              <Alert severity="warning">
                The selection spans tenants: this block only changes bookings of{' '}
                <strong>{valueTenantCode ?? 'the value’s tenant'}</strong>. Other bookings are skipped for this
                field (their other fields still change).
              </Alert>
            )}

            {block.field === 'tour' && block.tour?.is_bespoke && (
              <Alert severity="warning">
                <strong>{block.tour.name}</strong> is a bespoke tour: moved bookings take its own start date and time.
                Date and Departure time blocks are disabled for this run.
              </Alert>
            )}
            {block.field === 'tour' && block.tour && !block.tour.is_bespoke && (
              <Typography variant="caption" color="text.secondary">
                Bookings moving from a bespoke tour keep that tour’s date and time — add Date and Departure time
                blocks to set them explicitly.
              </Typography>
            )}
            {block.field === 'tour' && (
              <ChoiceField
                label="Tags in categories that belong only to the old tour"
                value={opts.oldTourTags}
                onChange={(v) => onOptsChange({ oldTourTags: v as RunOptions['oldTourTags'] })}
                options={[
                  { value: 'drop', label: 'Drop them' },
                  { value: 'keep', label: 'Keep them (the new tour’s editors won’t show them)' },
                ]}
              />
            )}
            {block.field === 'order_ref' && (
              <ChoiceField
                label="Order"
                value={opts.orderLink}
                onChange={(v) => onOptsChange({ orderLink: v as RunOptions['orderLink'] })}
                options={[
                  { value: 'move', label: 'Move to the order with this ref (created if missing)' },
                  { value: 'keep', label: 'Keep the current order (only the ref text changes)' },
                ]}
              />
            )}
            {block.field === 'booking_state' && block.enumValue === STATE_CANCELLED && (
              <>
                <ChoiceField
                  label="How to cancel"
                  value={block.cancelMode}
                  onChange={(v) => onChange({ cancelMode: v as CancelMode })}
                  options={catalog.options.cancel_modes.map((m) => ({ value: m.value, label: m.label }))}
                />
                {block.cancelMode && (
                  <Alert severity={block.cancelMode === 'cancel_with_event' ? 'warning' : 'info'}>
                    {CANCEL_MODE_WARNING[block.cancelMode]}
                  </Alert>
                )}
              </>
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
}

export function ChoiceField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string | null;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <FormControl size="small" required error={value == null}>
      <FormLabel sx={{ typography: 'body2' }}>{label}</FormLabel>
      <RadioGroup value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <FormControlLabel
            key={o.value}
            value={o.value}
            control={<Radio size="small" />}
            label={<Typography variant="body2">{o.label}</Typography>}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
}

/** Picker identity of a tour (name, pk, tenant, custom id, kind, zone) — shared with
 *  the record editor's tour picker. */
export function tourMeta(t: BookingEditTourOption) {
  return {
    name: t.name,
    pk: t.id,
    tenant: t.tenant?.code ?? null,
    extra: [t.custom_id, t.is_bespoke ? 'bespoke' : 'recurring', t.start_timezone],
  };
}

/** Picker identity of a staff role — shared with the record editor's role picker. */
export function roleMeta(r: BookingEditRoleOption) {
  return { name: r.name, pk: r.id, tenant: r.tenant?.code ?? null };
}

function ValueEditor({
  spec,
  block,
  onChange,
  tourQuery,
  roleQuery,
}: {
  spec: BookingEditCatalogField;
  block: BlockDraft;
  onChange: (patch: Partial<BlockDraft>) => void;
  tourQuery: (q: string, signal: AbortSignal) => Promise<BookingEditTourOption[]>;
  roleQuery: (q: string, signal: AbortSignal) => Promise<BookingEditRoleOption[]>;
}) {
  if (block.op === 'shift') {
    const unit = spec.shift_unit ?? 'minutes';
    return (
      <TextField
        size="small"
        type="number"
        label={`Shift by (${unit}, negative = earlier)`}
        value={block.shift}
        onChange={(e) => onChange({ shift: e.target.value })}
        sx={{ maxWidth: 280 }}
      />
    );
  }
  switch (spec.widget) {
    case 'tour':
      return (
        <RemoteAutocomplete<BookingEditTourOption>
          size="small"
          label="Target tour"
          placeholder="Type a tour name or id"
          queryFn={tourQuery}
          getOptionKey={(t) => t.id}
          getOptionLabel={(t) => t.name}
          getOptionMeta={tourMeta}
          value={block.tour}
          onChange={(v) => onChange({ tour: (v as BookingEditTourOption | null) ?? null })}
        />
      );
    case 'role':
      return (
        <Stack spacing={1}>
          <RadioGroup
            row
            value={block.roleClear ? 'clear' : 'set'}
            onChange={(e) => onChange({ roleClear: e.target.value === 'clear', role: null })}
          >
            <FormControlLabel value="set" control={<Radio size="small" />} label="Set a role" />
            <FormControlLabel value="clear" control={<Radio size="small" />} label="Clear the role (guest)" />
          </RadioGroup>
          {!block.roleClear && (
            <RemoteAutocomplete<BookingEditRoleOption>
              size="small"
              label="Role"
              fetchOnEmpty
              queryFn={roleQuery}
              getOptionKey={(r) => r.id}
              getOptionLabel={(r) => r.name}
              getOptionMeta={roleMeta}
              value={block.role}
              onChange={(v) => onChange({ role: (v as BookingEditRoleOption | null) ?? null })}
            />
          )}
        </Stack>
      );
    case 'date':
      return (
        <DatePicker
          label="Date (in each booking’s tour timezone)"
          value={block.when}
          onChange={(d) => onChange({ when: d })}
          slotProps={{ textField: { size: 'small', sx: { maxWidth: 280 } } }}
        />
      );
    case 'time':
      return (
        <TimePicker
          label="Departure time (in each booking’s tour timezone)"
          value={block.when}
          ampm={false}
          onChange={(d) => onChange({ when: d })}
          slotProps={{ textField: { size: 'small', sx: { maxWidth: 320 } } }}
        />
      );
    case 'datetime':
      return (
        <DateTimePicker
          label="End (in each booking’s tour timezone)"
          value={block.when}
          ampm={false}
          onChange={(d) => onChange({ when: d })}
          slotProps={{ textField: { size: 'small', sx: { maxWidth: 320 } } }}
        />
      );
    case 'enum':
      return (
        <TextField
          select
          size="small"
          label={spec.label}
          value={block.enumValue ?? ''}
          onChange={(e) => onChange({ enumValue: Number(e.target.value) })}
          sx={{ maxWidth: 320 }}
        >
          {spec.choices.map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </TextField>
      );
    case 'text':
      return (
        <TextField
          size="small"
          label={spec.label}
          value={block.text}
          onChange={(e) => onChange({ text: e.target.value })}
          inputProps={spec.max_length ? { maxLength: spec.max_length } : undefined}
          helperText={spec.nullable ? 'Leave blank to clear.' : undefined}
          fullWidth
        />
      );
    case 'integer':
      return (
        <TextField
          size="small"
          type="number"
          label={spec.label}
          value={block.integer}
          onChange={(e) => onChange({ integer: e.target.value })}
          inputProps={{ min: spec.min_value ?? undefined, max: spec.max_value ?? undefined }}
          sx={{ maxWidth: 200 }}
        />
      );
    case 'boolean':
      return (
        <Box>
          <RadioGroup
            row
            value={block.bool == null ? '' : block.bool ? 'yes' : 'no'}
            onChange={(e) => onChange({ bool: e.target.value === 'yes' })}
            aria-label={spec.label}
          >
            <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
            <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
          </RadioGroup>
        </Box>
      );
    default:
      return null;
  }
}
